/**
 * CustomPagination
 *
 * Design (matches screenshot):
 *   < 1 2 3 … 169 >   +   [10 ∨]
 *   - Active page: solid purple (#8660F8), white text, rounded square
 *   - Inactive pages: dark surface, muted text, rounded square
 *   - Ellipsis (…) shown when pages are far apart
 *   - First / last page always visible
 *   - Prev/Next arrows disabled + faded at boundaries
 *
 * Two usage modes:
 *   1. AG Grid:    <CustomPagination gridApi={gridApi} />
 *   2. Plain data: <CustomPagination total={n} page={p} pageSize={s}
 *                    onPageChange={fn} onPageSizeChange={fn} />
 */

import React, { useEffect, useState } from "react";

// ── Style tokens ──────────────────────────────────────────────────────────────
const BASE = {
  width: 38,
  height: 38,
  borderRadius: 10,
  border: "1px solid rgba(169,163,201,0.15)",
  background: "rgba(169,163,201,0.07)",
  color: "var(--text-muted, #A9A3C9)",
  cursor: "pointer",
  display: "inline-flex",
  alignItems: "center",
  justifyContent: "center",
  fontSize: 13,
  fontWeight: 500,
  fontFamily: "var(--font, 'Outfit', sans-serif)",
  transition: "background 0.15s, color 0.15s, box-shadow 0.15s",
  userSelect: "none",
  flexShrink: 0,
  outline: "none",
};

const ACTIVE = {
  ...BASE,
  background: "#8660F8",
  border: "none",
  color: "#fff",
  fontWeight: 700,
  boxShadow: "0 4px 14px rgba(134,96,248,0.45)",
};

const DISABLED = {
  ...BASE,
  opacity: 0.3,
  cursor: "not-allowed",
  pointerEvents: "none",
};

const ELLIPSIS = {
  ...BASE,
  cursor: "default",
  pointerEvents: "none",
  border: "none",
  background: "transparent",
  color: "var(--text-muted, #A9A3C9)",
};

const SELECT = {
  height: 38,
  minWidth: 82,
  padding: "0 30px 0 14px",
  borderRadius: 10,
  border: "1px solid rgba(169,163,201,0.15)",
  background: "rgba(169,163,201,0.07)",
  color: "var(--text-primary, #E3E2EB)",
  fontSize: 13,
  fontWeight: 600,
  fontFamily: "var(--font, 'Outfit', sans-serif)",
  outline: "none",
  cursor: "pointer",
  appearance: "none",
  WebkitAppearance: "none",
};

const PAGE_SIZES = [10, 20, 50, 100];

// ── Build the visible page list with ellipsis ─────────────────────────────────
// Returns an array of numbers or the string "…"
// Pattern: always show first, last, current ±1, with "…" gaps
function buildPages(current, total) {
  if (total <= 7) {
    return Array.from({ length: total }, (_, i) => i);
  }

  const pages = new Set();
  pages.add(0);               // always first
  pages.add(total - 1);       // always last
  pages.add(current);         // current
  if (current - 1 >= 0) pages.add(current - 1);
  if (current + 1 < total)  pages.add(current + 1);

  const sorted = [...pages].sort((a, b) => a - b);

  // Insert "…" where gaps > 1
  const result = [];
  for (let i = 0; i < sorted.length; i++) {
    if (i > 0 && sorted[i] - sorted[i - 1] > 1) {
      result.push("…");
    }
    result.push(sorted[i]);
  }
  return result;
}

// ── Component ─────────────────────────────────────────────────────────────────
const CustomPagination = ({
  // AG Grid mode
  gridApi,
  // Plain array mode
  total,
  page,
  pageSize: pageSizeProp,
  onPageChange,
  onPageSizeChange,
}) => {
  const isGrid = !!gridApi;

  // AG Grid state
  const [gPage,  setGPage]  = useState(0);
  const [gTotal, setGTotal] = useState(0);
  const [gSize,  setGSize]  = useState(10);

  useEffect(() => {
    if (!gridApi) return;
    const sync = () => {
      setGTotal(gridApi.paginationGetTotalPages());
      setGPage(gridApi.paginationGetCurrentPage());
    };
    sync();
    gridApi.addEventListener("paginationChanged", sync);
    return () => gridApi.removeEventListener("paginationChanged", sync);
  }, [gridApi]);

  // Resolved values
  const currentPage = isGrid ? gPage  : (page ?? 0);
  const totalPages  = isGrid ? gTotal : Math.max(1, Math.ceil((total ?? 0) / (pageSizeProp ?? 10)));
  const currentSize = isGrid ? gSize  : (pageSizeProp ?? 10);

  const goTo = (p) => {
    if (p < 0 || p >= totalPages) return;
    if (isGrid) {
      gridApi.paginationGoToPage(p);
      setGPage(p);
    } else {
      onPageChange?.(p);
    }
  };

  const handleSize = (e) => {
    const s = Number(e.target.value);
    if (isGrid) {
      setGSize(s);
      gridApi.setGridOption("paginationPageSize", s);
      gridApi.paginationGoToPage(0);
    } else {
      onPageSizeChange?.(s);
      onPageChange?.(0);
    }
  };

  const pages = buildPages(currentPage, totalPages);

  return (
    <div style={{ display: "flex", alignItems: "center", gap: 6, flexWrap: "wrap" }}>

      {/* ← Prev */}
      <button
        style={currentPage === 0 ? DISABLED : BASE}
        onClick={() => goTo(currentPage - 1)}
        title="Previous"
      >
        <i className="fa-solid fa-chevron-left" style={{ fontSize: 11 }} />
      </button>

      {/* Page numbers + ellipsis */}
      {pages.map((p, idx) =>
        p === "…" ? (
          <span key={`ellipsis-${idx}`} style={ELLIPSIS}>…</span>
        ) : (
          <button
            key={p}
            style={p === currentPage ? ACTIVE : BASE}
            onClick={() => goTo(p)}
          >
            {p + 1}
          </button>
        )
      )}

      {/* → Next */}
      <button
        style={currentPage >= totalPages - 1 ? DISABLED : BASE}
        onClick={() => goTo(currentPage + 1)}
        title="Next"
      >
        <i className="fa-solid fa-chevron-right" style={{ fontSize: 11 }} />
      </button>

      {/* Page size selector */}
      <div style={{ position: "relative", marginLeft: 6 }}>
        <select value={currentSize} onChange={handleSize} style={SELECT}>
          {PAGE_SIZES.map(s => (
            <option key={s} value={s}>{s}</option>
          ))}
        </select>
        <i
          className="fa-solid fa-chevron-down"
          style={{
            position: "absolute",
            right: 10,
            top: "50%",
            transform: "translateY(-50%)",
            fontSize: 10,
            color: "var(--text-muted, #A9A3C9)",
            pointerEvents: "none",
          }}
        />
      </div>

    </div>
  );
};

export default CustomPagination;
